import { type ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { cx } from "@/utils/cx";
import { useRangeSlider } from "./use-range-slider";
import { getFormatter, type ValueFormat, type ValueFormatterStrategy } from "./value-formatter";

export interface HistogramSliderProps {
  min: number;
  max: number;
  minValue: number;
  maxValue: number;
  onMinChange: (value: number) => void;
  onMaxChange: (value: number) => void;
  histogramData?: number[];
  format?: ValueFormat;
  formatter?: ValueFormatterStrategy;
  step?: number;
  className?: string;
  histogramClassName?: string;
  trackClassName?: string;
  inputClassName?: string;
  showInputs?: boolean;
  showHistogram?: boolean;
  minInputPlaceholder?: string;
  maxInputPlaceholder?: string;
}

const DEFAULT_HISTOGRAM_DATA = [0.3, 0.5, 0.7, 0.9, 1.0, 0.85, 0.6, 0.4, 0.25, 0.15, 0.1, 0.05];

export const HistogramSlider = ({
  min,
  max,
  minValue,
  maxValue,
  onMinChange,
  onMaxChange,
  histogramData = DEFAULT_HISTOGRAM_DATA,
  format = "number",
  formatter: customFormatter,
  step = 1,
  className,
  histogramClassName,
  trackClassName,
  inputClassName,
  showInputs = true,
  showHistogram = true,
  minInputPlaceholder,
  maxInputPlaceholder,
}: HistogramSliderProps) => {
  const formatter = useMemo(() => customFormatter ?? getFormatter(format), [customFormatter, format]);

  // Optimistic UI state - updated immediately during dragging
  const [optimisticMinValue, setOptimisticMinValue] = useState(minValue);
  const [optimisticMaxValue, setOptimisticMaxValue] = useState(maxValue);

  // Sync optimistic values when props change (from outside updates)
  useEffect(() => {
    setOptimisticMinValue(minValue);
  }, [minValue]);

  useEffect(() => {
    setOptimisticMaxValue(maxValue);
  }, [maxValue]);

  const { trackRef, isDragging, handleMinThumbMouseDown, handleMaxThumbMouseDown, getMinPercent, getMaxPercent } =
    useRangeSlider({
      min,
      max,
      minValue: optimisticMinValue,
      maxValue: optimisticMaxValue,
      onMinChange,
      onMaxChange,
      // Callbacks for optimistic UI updates during dragging
      onMinUIUpdate: setOptimisticMinValue,
      onMaxUIUpdate: setOptimisticMaxValue,
      step,
    });

  const [minInputValue, setMinInputValue] = useState(formatter.format(optimisticMinValue));
  const [maxInputValue, setMaxInputValue] = useState(formatter.format(optimisticMaxValue));

  // Keep input values in sync with optimistic slider values during dragging
  useEffect(() => {
    setMinInputValue(formatter.format(optimisticMinValue));
  }, [optimisticMinValue, formatter]);

  useEffect(() => {
    setMaxInputValue(formatter.format(optimisticMaxValue));
  }, [optimisticMaxValue, formatter]);

  const minPercent = getMinPercent();
  const maxPercent = getMaxPercent();

  const isBarActive = useCallback(
    (index: number) => {
      const barPercent = (index / histogramData.length) * 100;
      const barEndPercent = ((index + 1) / histogramData.length) * 100;
      return barPercent >= minPercent && barEndPercent <= maxPercent;
    },
    [minPercent, maxPercent, histogramData.length],
  );

  const handleMinInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMinInputValue(e.target.value);
  }, []);

  const handleMaxInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setMaxInputValue(e.target.value);
  }, []);

  const handleMinInputBlur = useCallback(() => {
    const parsed = formatter.parse(minInputValue);
    const clamped = Math.max(min, Math.min(parsed, optimisticMaxValue));
    onMinChange(clamped);
    setOptimisticMinValue(clamped);
    setMinInputValue(formatter.format(clamped));
  }, [formatter, minInputValue, min, optimisticMaxValue, onMinChange]);

  const handleMaxInputBlur = useCallback(() => {
    const parsed = formatter.parse(maxInputValue);
    const clamped = Math.max(optimisticMinValue, Math.min(parsed, max));
    onMaxChange(clamped);
    setOptimisticMaxValue(clamped);
    setMaxInputValue(formatter.format(clamped));
  }, [formatter, maxInputValue, max, optimisticMinValue, onMaxChange]);

  const handleMinInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleMinInputBlur();
      }
    },
    [handleMinInputBlur],
  );

  const handleMaxInputKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      if (e.key === "Enter") {
        handleMaxInputBlur();
      }
    },
    [handleMaxInputBlur],
  );

  return (
    <div className={cx("w-full px-2.5", className)}>
      {showHistogram && (
        <div className={cx("flex items-end h-10 gap-0.5 mb-1", histogramClassName)}>
          {histogramData.map((value, index) => {
            const barId = `bar-${index}-${value}`;
            return (
              <div
                key={barId}
                className={cx(
                  "flex-1 rounded-sm transition-colors duration-150",
                  isBarActive(index) ? "bg-gray-800" : "bg-gray-200",
                )}
                style={{ height: `${value * 100}%` }}
              />
            );
          })}
        </div>
      )}

      <div ref={trackRef} className={cx("relative h-1 bg-gray-200 rounded-full my-3", trackClassName)}>
        <div
          className="absolute h-full bg-gray-800 rounded-full"
          style={{
            left: `${minPercent}%`,
            width: `${maxPercent - minPercent}%`,
          }}
        />

        <div
          role="slider"
          tabIndex={0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={optimisticMinValue}
          aria-label="Minimum value"
          className={cx(
            "absolute w-5 h-5 bg-white border-2 border-gray-800 rounded-full cursor-pointer",
            "top-1/2 -translate-y-1/2 -translate-x-1/2",
            optimisticMinValue === optimisticMaxValue ? "z-20" : "z-10",
            "hover:shadow-[0_0_0_4px_rgba(31,41,55,0.1)]",
            "focus:outline-none focus:shadow-[0_0_0_4px_rgba(31,41,55,0.2)]",
            isDragging === "min" && "shadow-[0_0_0_4px_rgba(31,41,55,0.15)]",
          )}
          style={{ left: `${minPercent}%` }}
          onMouseDown={handleMinThumbMouseDown}
          onTouchStart={handleMinThumbMouseDown}
        />

        <div
          role="slider"
          tabIndex={0}
          aria-valuemin={min}
          aria-valuemax={max}
          aria-valuenow={optimisticMaxValue}
          aria-label="Maximum value"
          className={cx(
            "absolute w-5 h-5 bg-white border-2 border-gray-800 rounded-full cursor-pointer",
            "top-1/2 -translate-y-1/2 -translate-x-1/2 z-10",
            "hover:shadow-[0_0_0_4px_rgba(31,41,55,0.1)]",
            "focus:outline-none focus:shadow-[0_0_0_4px_rgba(31,41,55,0.2)]",
            isDragging === "max" && "shadow-[0_0_0_4px_rgba(31,41,55,0.15)]",
          )}
          style={{ left: `${maxPercent}%` }}
          onMouseDown={handleMaxThumbMouseDown}
          onTouchStart={handleMaxThumbMouseDown}
        />
      </div>

      {showInputs && (
        <div className="flex justify-between gap-4">
          <input
            type="text"
            value={minInputValue}
            onChange={handleMinInputChange}
            onBlur={handleMinInputBlur}
            onKeyDown={handleMinInputKeyDown}
            placeholder={minInputPlaceholder}
            className={cx(
              "w-24 px-3 py-2.5 text-center text-sm bg-primary",
              "border border-gray-200 rounded-lg",
              "outline-none focus:border-gray-400",
              inputClassName,
            )}
          />
          <input
            type="text"
            value={maxInputValue}
            onChange={handleMaxInputChange}
            onBlur={handleMaxInputBlur}
            onKeyDown={handleMaxInputKeyDown}
            placeholder={maxInputPlaceholder}
            className={cx(
              "w-24 px-3 py-2.5 text-center text-sm bg-primary",
              "border border-gray-200 rounded-lg",
              "outline-none focus:border-gray-400",
              inputClassName,
            )}
          />
        </div>
      )}
    </div>
  );
};
